import { Money } from '../dinheiro/money.js'
import { moeda } from '../dinheiro/moeda.js'
import { dataNominal, diasEntre } from '../data/data-nominal.js'

export type DirecaoDaConciliacao = 'credito' | 'debito'

export interface MovimentoParaSugestao {
  readonly valor: string
  readonly data: string
  readonly direcao: DirecaoDaConciliacao
  readonly historico: string | null
  readonly contraparteNome: string | null
  readonly contraparteDocumento: string | null
}

export interface CandidatoDaConciliacao {
  // 'previsao': a previsão de caixa aberta (DS-620), pelo restante.
  readonly tipo: 'recebivel' | 'adiantamento' | 'conta_pagar' | 'comissao' | 'rh' | 'transferencia' | 'avulso' | 'previsao'
    | 'baixa_registrada_pagar' | 'baixa_registrada_rh'
  readonly id: string
  readonly referencia: string
  readonly nome: string
  readonly documento: string | null
  readonly valor: string | null
  readonly data: string | null
  readonly direcao: DirecaoDaConciliacao | null
}

export interface SinalDaConciliacao {
  readonly codigo: 'direcao' | 'documento' | 'nome' | 'valor' | 'data' | 'empate'
  readonly bateu: boolean
  readonly texto: string
}

export interface CandidatoPontuado extends CandidatoDaConciliacao {
  readonly score: number
  readonly sinais: readonly SinalDaConciliacao[]
}

export interface ResultadoDaSugestao {
  readonly principal: CandidatoPontuado | null
  readonly outros: readonly CandidatoPontuado[]
  readonly empate: boolean
}

const BRL = moeda('BRL', 2)

const soAlfanumerico = (valor: string | null): string =>
  (valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

function documentoCasa(movimento: string | null, candidato: string | null): boolean {
  const recebido = soAlfanumerico(movimento)
  const cadastrado = soAlfanumerico(candidato)
  if (recebido === '' || cadastrado === '') return false
  if (recebido === cadastrado) return true

  // CPF mascarado do Sicoob: os seis dígitos centrais são os dígitos 4–9 do CPF.
  const visiveis = (movimento ?? '').replace(/\D/g, '')
  return (movimento ?? '').includes('*') && visiveis.length === 6 && cadastrado.slice(3, 9) === visiveis
}

function nomeCasa(movimento: string | null, candidato: string): boolean {
  const recebido = soAlfanumerico(movimento)
  const cadastrado = soAlfanumerico(candidato)
  return recebido.length >= 5 && cadastrado.length >= 5
    && (recebido.startsWith(cadastrado) || cadastrado.startsWith(recebido))
}

function distanciaEmDias(a: string, b: string): number {
  return Math.abs(diasEntre(dataNominal(a), dataNominal(b)))
}

function calcularScore(
  temIdentidade: boolean,
  valorExato: boolean,
  dias: number | null,
  dataNaJanela: boolean,
): number {
  if (temIdentidade) {
    if (valorExato) {
      if (dias === 0) return 100
      return dataNaJanela ? 99 : 95
    }
    if (dias === 0) return 85
    return dataNaJanela ? 84 : 80
  }
  if (valorExato && dataNaJanela) return 79
  if (valorExato) return 60
  if (dataNaJanela) return 19
  return 0
}

function pontuar(movimento: MovimentoParaSugestao, candidato: CandidatoDaConciliacao): CandidatoPontuado | null {
  if (candidato.tipo === 'transferencia') {
    if (candidato.direcao === null || candidato.direcao === movimento.direcao
      || candidato.data !== movimento.data || candidato.valor === null
      || !Money.de(candidato.valor, BRL).igualA(Money.de(movimento.valor, BRL))) return null
  } else if (candidato.direcao !== null && candidato.direcao !== movimento.direcao) return null

  const sinais: SinalDaConciliacao[] = [
    { codigo: 'direcao', bateu: true, texto: 'Direção compatível' },
  ]
  const porDocumento = documentoCasa(movimento.contraparteDocumento, candidato.documento)
  const porNome = nomeCasa(movimento.contraparteNome, candidato.nome)
  if (porDocumento) sinais.push({ codigo: 'documento', bateu: true, texto: 'Documento da contraparte confere' })
  else if (porNome) sinais.push({ codigo: 'nome', bateu: true, texto: 'Nome da contraparte confere por prefixo' })

  const valorExato = candidato.valor !== null
    && Money.de(candidato.valor, BRL).igualA(Money.de(movimento.valor, BRL))
  sinais.push({ codigo: 'valor', bateu: valorExato, texto: valorExato ? 'Valor exato' : 'Valor diverge' })

  const distancia = candidato.data === null ? null : distanciaEmDias(movimento.data, candidato.data)
  const dataNaJanela = distancia !== null && distancia <= 7
  sinais.push({
    codigo: 'data',
    bateu: dataNaJanela,
    texto: dataNaJanela ? 'Data dentro da janela de 7 dias' : 'Data fora da janela de 7 dias',
  })

  // Documento identifica a contraparte, mas pode haver várias parcelas dela abertas.
  // Por isso o alvo só chega a 100 quando identidade, valor e janela apontam juntos;
  // sem identidade, valor+data jamais ultrapassam 79 (DS-131).
  const score = calcularScore(porDocumento || porNome, valorExato, distancia, dataNaJanela)
  return score === 0 ? null : { ...candidato, score, sinais }
}

/**
 * Motor puro: ordena e explica, sem gravar estado. Empate no topo não escolhe um
 * vencedor; todos voltam como alternativas âmbar.
 */
export function sugerirConciliacao(
  movimento: MovimentoParaSugestao,
  candidatos: readonly CandidatoDaConciliacao[],
): ResultadoDaSugestao {
  const pontuados = candidatos
    .map((c) => pontuar(movimento, c))
    .filter((c): c is CandidatoPontuado => c !== null)
    .sort((a, b) => a.score === b.score
      ? a.referencia.localeCompare(b.referencia, 'pt-BR')
      : a.score > b.score ? -1 : 1)
  const topo = pontuados[0]?.score
  if (topo === undefined) return { principal: null, outros: [], empate: false }
  const empatados = pontuados.filter((c) => c.score === topo)
  if (empatados.length > 1) {
    return {
      principal: null,
      outros: empatados.map((c) => ({
        ...c,
        score: Math.min(c.score, 79),
        sinais: [...c.sinais, { codigo: 'empate', bateu: false, texto: 'Há mais de um candidato com o mesmo score' }],
      })),
      empate: true,
    }
  }
  return { principal: pontuados[0] ?? null, outros: pontuados.slice(1), empate: false }
}
